import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  ArrowLeft, 
  Upload, 
  Sparkles, 
  Mic, 
  Download, 
  Share2, 
  FolderPlus,
  Users,
  FileText,
  Play,
  CheckCircle
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cc23a1c1-e998-45d0-adb8-2e9c1c544201/artifacts/g7bc3tyb_image.png";

const HelpPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Upload,
      title: "Upload Videos",
      description: "Upload one or multiple videos to your project. Supported formats: MP4, WebM, MOV."
    },
    {
      icon: Sparkles,
      title: "AI Analysis",
      description: "Our AI analyses your videos, extracts key frames, transcribes audio, and generates step-by-step documentation."
    },
    {
      icon: Mic,
      title: "Auto Voice-over",
      description: "Voice-overs are automatically generated in Australian English when you analyse videos. Preview and edit as needed."
    },
    {
      icon: FileText,
      title: "Edit & Customise",
      description: "Edit step titles, descriptions, reorder steps with drag-and-drop, and add your own content."
    },
    {
      icon: Download,
      title: "Export Options",
      description: "Export as professional PDF or Word documents. Download videos with AI voice-overs embedded."
    },
    {
      icon: Share2,
      title: "Share & Collaborate",
      description: "Share projects with team members (view/edit permissions) or create public read-only links."
    }
  ];

  const quickSteps = [
    "Create a new project from your Dashboard",
    "Upload your video(s) in the Editor",
    "Click 'Analyse with AI' to generate documentation",
    "Edit steps and review the auto-generated voice-over",
    "Export as PDF/Word or download videos with AI voice",
    "Share with your team or via public link"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="hover:bg-slate-100 text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <img src={LOGO_URL} alt="FlowSnap" className="h-10" />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            How to Use FlowSnap
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Transform your videos into professional step-by-step documentation with AI-powered voice-overs.
          </p>
        </div>

        {/* Quick Start */}
        <Card className="p-8 mb-12 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Play className="w-6 h-6 text-blue-600" />
            Quick Start
          </h2>
          <div className="grid gap-4">
            {quickSteps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-semibold">
                  {idx + 1}
                </div>
                <p className="text-slate-700 text-lg pt-1">{step}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Features Grid */}
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Key Features</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {features.map((feature, idx) => (
            <Card key={idx} className="p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
              <p className="text-slate-600">{feature.description}</p>
            </Card>
          ))}
        </div>

        {/* Tips */}
        <Card className="p-8 bg-amber-50 border-amber-200">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Pro Tips</h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700"><strong>Multiple videos:</strong> Upload all related videos to one project for a comprehensive guide.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700"><strong>Edit voice-over text:</strong> Modify the transcription and regenerate for better narration.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700"><strong>Preview sync:</strong> Use the Voice-over tab to preview video with AI voice before downloading.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700"><strong>Organise content:</strong> Use folders and teams to keep projects organised and accessible.</span>
            </li>
          </ul>
        </Card>

        {/* Full Documentation Link */}
        <div className="text-center mt-12">
          <p className="text-slate-600 mb-4">Need more detailed instructions?</p>
          <Button 
            variant="outline"
            onClick={() => window.open('/docs/user-guide.md', '_blank')}
            className="border-slate-300"
          >
            <FileText className="w-4 h-4 mr-2" />
            View Full User Guide
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 mt-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-slate-500">&copy; 2026 FlowSnap. Transforming video knowledge into actionable documentation.</p>
        </div>
      </footer>
    </div>
  );
};

export default HelpPage;
